/** Pure advisory matching; a person confirms the selected servicer on the recon. */
export interface ServicerSuggestionInput {
  candidates: Array<
    {
      templateId: string;
      templateName: string;
      paServicerCodes: string[];
      propertyStates: string[];
    }
  >;
  purchaseAdviceText: string | null;
  propertyState: string | null;
}
export interface ServicerSuggestion {
  templateId: string | null;
  source: 'auto_single' | 'suggested_pa' | 'suggested_state' | null;
  evidence: { matchedCode?: string; snippet?: string; state?: string } | null;
  reason:
    | 'auto_single'
    | 'pa_match'
    | 'state_match'
    | 'ambiguous_pa'
    | 'ambiguous_state'
    | 'no_match';
}
export function suggestServicerTemplate(input: ServicerSuggestionInput): ServicerSuggestion {
  const none = (reason: ServicerSuggestion['reason']): ServicerSuggestion => ({
    templateId: null,
    source: null,
    evidence: null,
    reason
  });
  if (input.candidates.length === 1) {
    return {
      templateId: input.candidates[0].templateId,
      source: 'auto_single',
      evidence: null,
      reason: 'auto_single'
    };
  }
  const text = input.purchaseAdviceText?.toUpperCase() ?? '';
  const matches = input.candidates.flatMap((candidate) => {
    for (const rawCode of candidate.paServicerCodes) {
      const code = rawCode.trim().toUpperCase();
      if (!code) continue;
      const match = new RegExp('(^|[^A-Z0-9])' + code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).exec(
        text
      );
      if (match) {
        const start = Math.max(0, match.index + match[1].length - 30);
        return [{
          templateId: candidate.templateId,
          code,
          snippet: input.purchaseAdviceText!.slice(start, start + 80)
        }];
      }
    }
    return [];
  });
  if (matches.length > 1) return none('ambiguous_pa');
  if (matches.length === 1) {
    const match = matches[0];
    return {
      templateId: match.templateId,
      source: 'suggested_pa',
      evidence: { matchedCode: match.code, snippet: match.snippet },
      reason: 'pa_match'
    };
  }
  const state = input.propertyState?.trim().toUpperCase();
  const stateMatches = state
    ? input.candidates.filter((c) => c.propertyStates.includes(state))
    : [];
  if (stateMatches.length > 1) return none('ambiguous_state');
  if (stateMatches.length === 1) {
    return {
      templateId: stateMatches[0].templateId,
      source: 'suggested_state',
      evidence: { state },
      reason: 'state_match'
    };
  }
  return none('no_match');
}
